import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Moment from 'moment';
import APIs from '../../Constants/apis';
import axiosInstance from '../../config/axiosApi/axiosApi';
import { encodeUrlParam } from '../../utils/urlUtils';
import useDebouncedValue from '../../hooks/useDebouncedValue';

const GlobalSearch = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebouncedValue(searchQuery.trim());
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const searchRef = useRef(null);
  const resultsRef = useRef(null);
  const latestSearchTokenRef = useRef(0);
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const { profileData } = useSelector((state) => state.auth);
  const [compCurrency, setCompCurrency] = useState("GBP");

  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
  };

  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return Moment(dateString).format('MMM DD, YYYY HH:mm');
    } catch (error) {
      return dateString;
    }
  };

  const fetchCompCurrency = useCallback(async () => {
    try {
      if (!profileData) {
        setCompCurrency("GBP");
        return;
      }
      setCompCurrency(profileData?.compCurrency || "GBP");
    } catch (err) {
      setCompCurrency("GBP");
    }
  }, [profileData]);

  useEffect(() => {
    if (debouncedSearchQuery.length >= 2) {
      performSearch(debouncedSearchQuery);
    } else {
      setSearchResults([]);
      setShowResults(false);
    }
    // performSearch reads CompID/compCurrency from closure; debounced query drives API timing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery, CompID]);

  useEffect(() => {
    fetchCompCurrency();
  }, [fetchCompCurrency]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const performSearch = async (query) => {
    if (!CompID) return;

    const searchToken = ++latestSearchTokenRef.current;
    setIsSearching(true);
    try {
      const response = await axiosInstance.get(APIs.GLOBAL_SEARCH, {
        params: { CompID, searchTerm: query }
      });

      // Ignore stale responses from older queries
      if (searchToken !== latestSearchTokenRef.current) return;

      if (response.data && response.data.status && response.data.data) {
        const { customers, orders, products, categories, discounts, modifierGroups, staff } = response.data.data;

        const results = [];

        if (customers && customers.length > 0) {
          results.push({
            type: 'customers',
            title: 'Customers',
            icon: 'fa fa-users',
            items: customers.map(customer => ({
              id: customer.cust_Id || 0,
              title: `${customer.cust_Fname || ''} ${customer.cust_Lname || ''}`.trim(),
              subtitle: customer.cust_Email || customer.cust_Phone || 'Customer',
              link: `/customer/updateCustomer/${customer.cust_Id || customer.cust_Id}`,
              type: 'customer'
            }))
          });
        }

        if (orders && orders.length > 0) {
          results.push({
            type: 'orders',
            title: 'Orders',
            icon: 'fa fa-shopping-cart',
            items: orders.map(order => ({
              id: order.recvorder_ID || order.recvOrder_ID,
              title: `Order #${order.recvorder_InvoiceNo || order.recvorder_InvoiceNo} - ${order.recvorder_FrontOrderID || order.recvorder_FrontOrderID}`,
              subtitle: `${getCurrencySymbol(compCurrency)}${order.recvorder_Totalamount || order.recvorder_Totalamount || '0.00'} - ${formatDate(order.recvorder_datetime || order.recvorder_datetime)}`,
              link: `/order/orderDetails/${order.recvorder_InvoiceNo ||order.recvorder_InvoiceNo || 0}`,
              type: 'order'
            }))
          });
        }

        if (products && products.length > 0) {
          results.push({
            type: 'products',
            title: 'Products',
            icon: 'fa fa-box',
            items: products.map(product => ({
              id: product.menuItem_ID || product.menuItem_ID,
              title: product.menuItem_Name || product.menuItem_Name,
              subtitle: `${getCurrencySymbol(compCurrency)} ${product.menuItem_SalePrice || product.menuItem_SalePrice || '0.00'}`,
              link: `/items/updateItem/${product.menuItem_ID || product.menuItem_ID}`,
              type: 'product'
            }))
          });
        }

        if (categories && categories.length > 0) {
          results.push({
            type: 'categories',
            title: 'Categories',
            icon: 'fa fa-tags',
            items: categories.map(category => ({
              id: category.rootCat_ID || category.rootCat_ID,
              title: category.rootCat_Name || category.rootCat_Name,
              subtitle: category.rootCat_Detail || category.rootCat_Detail,
              link: `/categories/updateCategory/${category.rootCat_ID || category.rootCat_ID}`,
              type: 'category'
            }))
          });
        }

        if (discounts && discounts.length > 0) {
          results.push({
            type: 'discounts',
            title: 'Discounts',
            icon: 'fa fa-percent',
            items: discounts.map(discount => ({
              id: discount.id || discount.id,
              title: discount.discountName || discount.discountName,
              subtitle: `${discount.discountPercentage || discount.discountPercentage || '0'}% - ${discount.minimumSpendAmount || discount.minimumSpendAmount || ''}`,
              link: `/discount/updateDiscount/${discount.id || discount.id}`,
              type: 'discount'
            }))
          });
        }

        if (modifierGroups && modifierGroups.length > 0) {
          results.push({
            type: 'modifierGroups',
            title: 'Modifier Groups',
            icon: 'fa fa-list',
            items: modifierGroups.map(group => ({
              id: group.modifierGroupID || group.modifierGroupID,
              title: group.name || group.name,
              subtitle: group.subTitle || group.subTitle || 'Modifier Group',
              link: `/modifiers/updateModifierGroup/${group.modifierGroupID || group.modifierGroupID}`,
              type: 'modifierGroup'
            }))
          });
        }

        if (staff && staff.length > 0) {
          results.push({
            type: 'staff',
            title: 'Staff',
            icon: 'fa fa-user-tie',
            items: staff.map(staff => ({
              id: staff.userName || staff.userName,
              title: `${staff.firstName || staff.firstName || ''} ${staff.lastName || staff.lastName || ''}`.trim(),
              subtitle: staff.email || staff.email || staff.position || staff.position || 'Staff',
              link: `/staff/updateStaff/${encodeUrlParam(staff.userName)}`,
              type: 'staff'
            }))
          });
        }

        setSearchResults(results);
        setShowResults(true);
      } else {
        setSearchResults([]);
        setShowResults(query.trim().length >= 2);
      }
    } catch (error) {
      if (searchToken !== latestSearchTokenRef.current) return;
      setSearchResults([]);
      setShowResults(query.trim().length >= 2);
    } finally {
      if (searchToken === latestSearchTokenRef.current) {
        setIsSearching(false);
      }
    }
  };

  const handleKeyDown = (e) => {
    if (!showResults || searchResults.length === 0) return;

    const totalItems = searchResults.reduce((sum, section) => sum + section.items.length, 0);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % totalItems);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev <= 0 ? totalItems - 1 : prev - 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          const selectedItem = getSelectedItem();
          if (selectedItem) {
            setShowResults(false);
            setSearchQuery('');
            window.location.href = selectedItem.link;
          }
        }
        break;
      case 'Escape':
        setShowResults(false);
        setSelectedIndex(-1);
        setSearchQuery('');
        break;
    }
  };

  const getSelectedItem = () => {
    let currentIndex = 0;
    for (const section of searchResults) {
      for (const item of section.items) {
        if (currentIndex === selectedIndex) {
          return item;
        }
        currentIndex++;
      }
    }
    return null;
  };

  const renderHighlightedText = (text, query) => {
    const source = String(text || "");
    if (!query) return source;

    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    if (!escapedQuery) return source;

    const regex = new RegExp(`(${escapedQuery})`, 'gi');
    const parts = source.split(regex);

    const normalizedQuery = query.toLowerCase();
    return parts.map((part, index) => (
      part.toLowerCase() === normalizedQuery ? <mark key={`${part}-${index}`}>{part}</mark> : <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
    ));
  };

  const totalResults = searchResults.reduce((sum, section) => sum + section.items.length, 0);

  return (
    <div className="navbar-form header-topbar-search" ref={searchRef}>
      <div className="position-relative">
        <div className="form-group d-flex align-items-center mb-0">
          {/* Visible label fallback — native ::placeholder is overridden by dark-theme CSS */}
          {!searchQuery && (
            <span className="header-topbar-search__ph" aria-hidden="true">
              Search
            </span>
          )}
          <input
            type="text"
            className="form-control header-topbar-search__input"
            placeholder="Search"
            aria-label="Search"
            value={searchQuery}
            onChange={(e) => {
              const value = e.target.value;
              setSearchQuery(value);
              if (value.trim().length < 2) {
                latestSearchTokenRef.current += 1; // invalidate any pending responses
                setSearchResults([]);
                setShowResults(false);
                setSelectedIndex(-1);
                setIsSearching(false);
              }
            }}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (searchResults.length > 0) {
                setShowResults(true);
              }
            }}
          />
          {isSearching ? (
            <div className="btn btn-search" aria-hidden="true">
              <div className="spinner-border spinner-border-sm" role="status">
                <span className="visually-hidden">Searching...</span>
              </div>
            </div>
          ) : (
            <button type="button" className="btn btn-search" aria-label="Search" tabIndex={-1}>
              {/* Figma Frame 1171277941 — exact search icon */}
              <svg
                className="header-topbar-search-icon"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <circle
                  cx="7.25"
                  cy="7.25"
                  r="5"
                  stroke="#4A4A4A"
                  strokeWidth="1.2"
                />
                <path
                  d="M11 11L14.25 14.25"
                  stroke="#4A4A4A"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          )}
        </div>

        {showResults && (
          <div className="search-results-panel" ref={resultsRef}>
            <div className="search-results-header">
              <div className="search-results-header-left">
                <span className="search-results-header-title">Search Results</span>
                <span className="search-results-header-meta">
                  {isSearching ? "Searching..." : `${totalResults} result${totalResults === 1 ? "" : "s"}`}
                </span>
              </div>
              <button
                className="btn btn-sm btn-link search-results-close-btn p-0"
                onClick={() => setShowResults(false)}
              >
                <i className="fa fa-times"></i>
              </button>
            </div>

            {!isSearching && searchResults.length > 0 && searchResults.map((section, sectionIndex) => (
              <div key={section.type} className="search-results-section">
                <div className="search-results-section-header">
                  <span className="search-section-icon-wrap">
                    <i className={`${section.icon}`}></i>
                  </span>
                  <span className="fw-bold">{section.title}</span>
                  <span className="badge search-section-count-badge ms-2">{section.items.length}</span>
                </div>

                {section.items.map((item, itemIndex) => {
                  const globalIndex = searchResults
                    .slice(0, sectionIndex)
                    .reduce((sum, s) => sum + s.items.length, 0) + itemIndex;

                  return (
                    <Link
                      key={item.id}
                      to={item.link}
                      className={`search-result-item ${selectedIndex === globalIndex ? 'selected' : ''}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowResults(false);
                        setSearchQuery('');
                        window.location.href = item.link;
                      }}
                    >
                      <div className="search-result-content">
                        <div className="search-result-title">
                          {renderHighlightedText(item.title, searchQuery)}
                        </div>
                        <div className="search-result-subtitle text-muted">
                          {item.subtitle}
                        </div>
                      </div>
                      <i className="fa fa-chevron-right search-result-arrow"></i>
                    </Link>
                  );
                })}
              </div>
            ))}

            {isSearching && (
              <div className="search-no-results text-center py-3">
                <div className="spinner-border spinner-border-sm me-2" role="status">
                  <span className="visually-hidden">Searching...</span>
                </div>
                <span className="text-muted">Searching for "{searchQuery}"...</span>
              </div>
            )}

            {!isSearching && searchResults.length === 0 && searchQuery.trim().length >= 2 && (
              <div className="search-no-results text-center py-3">
                <i className="fa fa-search text-muted mb-2"></i>
                <div className="text-muted">No results found for "{searchQuery}"</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GlobalSearch;